/**
 * OHSSCheckIn — Daily OHSS risk check-in modal.
 *
 * Captures bloating severity, weight, pain level, nausea, and symptoms.
 */

import React, { useState, useCallback } from "react";
import { Modal, Pressable, ScrollView, View, TextInput } from "react-native";
import { Text } from "@src/packages/ui/Text/Text";
import { X, Check, Loader2 } from "lucide-react-native";
import { useLogSymptoms } from "../hooks/useSymptomLogs";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

interface OHSSCheckInProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

type SaveState = "idle" | "saving" | "saved";

const NAUSEA_OPTIONS = ["none", "mild", "moderate", "severe"] as const;
const SYMPTOM_OPTIONS = [
  "Dizziness",
  "Headache",
  "Constipation",
  "Fatigue",
  "Spotting",
  "Shortness of breath",
  "Vomiting",
  "Reduced urine",
];

export function OHSSCheckIn({ open, onClose, onSaved }: OHSSCheckInProps) {
  const capturePress = usePressCapture("OHSSCheckIn");
  const [bloating, setBloating] = useState(1);
  const [pain, setPain] = useState(1);
  const [weight, setWeight] = useState("");
  const [nausea, setNausea] = useState<(typeof NAUSEA_OPTIONS)[number]>("none");
  const [symptoms, setSymptoms] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState(false);
  const logSymptoms = useLogSymptoms();

  const toggleSymptom = (s: string) => {
    setSymptoms((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const resetForm = useCallback(() => {
    setBloating(1);
    setPain(1);
    setWeight("");
    setNausea("none");
    setSymptoms(new Set());
    setNotes("");
    setSaveState("idle");
    setSaveError(false);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveState("saving");
    setSaveError(false);

    // Persist through the real symptoms API — the old local recovery-log
    // stub silently discarded OHSS check-ins while showing "saved" (#2246).
    const sharedNotes = notes.trim() || undefined;
    const rows: { symptom: string; notes?: string }[] = [
      { symptom: `OHSS check-in: bloating ${bloating}/5`, notes: sharedNotes },
      { symptom: `OHSS check-in: pain ${pain}/5` },
      ...(nausea !== "none"
        ? [{ symptom: `OHSS check-in: nausea (${nausea})` }]
        : []),
      ...(weight ? [{ symptom: `OHSS check-in: weight ${weight} kg` }] : []),
      ...Array.from(symptoms).map((s) => ({ symptom: s })),
    ];

    try {
      await logSymptoms.mutateAsync({ symptoms: rows });
      setSaveState("saved");
      setTimeout(() => {
        resetForm();
        onSaved?.();
      }, 600);
    } catch {
      // Keep the draft on screen: the same button retries. A silent reset
      // here would fake a successful clinical check-in.
      setSaveState("idle");
      setSaveError(true);
    }
  }, [
    bloating,
    pain,
    weight,
    nausea,
    symptoms,
    notes,
    logSymptoms,
    resetForm,
    onSaved,
  ]);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  return (
    <Modal visible={open} animationType="slide" transparent>
      <View className="flex-1 bg-black/60">
        <View className="mt-auto max-h-[90%] rounded-t-3xl bg-[#0f172a]">
          {/* Header */}
          <View className="flex-row items-center justify-between border-b border-white/10 px-5 py-4">
            <Text className="text-lg font-semibold text-white">
              Daily Recovery Check-in
            </Text>
            <Pressable
              onPress={() => {
                capturePress("close_recovery_check_in");
                handleClose();
              }}
              className="rounded-full bg-white/10 p-2"
            >
              <X size={18} color="rgba(255,255,255,0.6)" />
            </Pressable>
          </View>

          <ScrollView
            className="px-5 py-4"
            contentContainerStyle={{ paddingBottom: 40, gap: 20 }}
          >
            {/* Bloating */}
            <View>
              <Text className="mb-2 text-sm font-semibold text-slate-300">
                Bloating Severity
              </Text>
              <View className="flex-row gap-2">
                {[1, 2, 3, 4, 5].map((level) => (
                  <Pressable
                    key={level}
                    onPress={() => {
                      capturePress(
                        pressLabel(String(level)),
                        "bloating_severity_option",
                      );
                      setBloating(level);
                    }}
                    className={`flex-1 items-center rounded-xl py-3 ${
                      bloating === level
                        ? level >= 4
                          ? "border border-red-500/40 bg-red-500/20"
                          : "border border-[#C4B5FD]/40 bg-[#C4B5FD]/20"
                        : "border border-white/10 bg-white/5"
                    }`}
                  >
                    <Text
                      className={`text-lg font-bold ${
                        bloating === level
                          ? level >= 4
                            ? "text-red-400"
                            : "text-[#C4B5FD]"
                          : "text-slate-500"
                      }`}
                    >
                      {level}
                    </Text>
                    <Text className="text-xs text-slate-500">
                      {level === 1
                        ? "None"
                        : level === 3
                          ? "Mod"
                          : level === 5
                            ? "Severe"
                            : ""}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Pain Level */}
            <View>
              <Text className="mb-2 text-sm font-semibold text-slate-300">
                Pain Level (1-10)
              </Text>
              <View className="flex-row flex-wrap gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => (
                  <Pressable
                    key={level}
                    onPress={() => {
                      capturePress(
                        pressLabel(String(level)),
                        "pain_severity_option",
                      );
                      setPain(level);
                    }}
                    className={`items-center justify-center rounded-lg ${
                      pain === level
                        ? level >= 7
                          ? "border border-red-500/40 bg-red-500/20"
                          : "border border-[#C4B5FD]/40 bg-[#C4B5FD]/20"
                        : "border border-white/10 bg-white/5"
                    }`}
                    style={{ width: 40, height: 40 }}
                  >
                    <Text
                      className={`text-sm font-semibold ${
                        pain === level
                          ? level >= 7
                            ? "text-red-400"
                            : "text-[#C4B5FD]"
                          : "text-slate-500"
                      }`}
                    >
                      {level}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Weight */}
            <View>
              <Text className="mb-1.5 text-sm font-semibold text-slate-300">
                Weight Today (optional)
              </Text>
              <View className="flex-row items-center gap-2">
                <TextInput
                  value={weight}
                  onChangeText={setWeight}
                  placeholder="—"
                  placeholderTextColor="rgba(255,255,255,0.2)"
                  keyboardType="decimal-pad"
                  className="w-24 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-center text-sm text-white"
                />
                <Text className="text-xs text-slate-500">kg</Text>
              </View>
            </View>

            {/* Nausea */}
            <View>
              <Text className="mb-2 text-sm font-semibold text-slate-300">
                Nausea
              </Text>
              <View className="flex-row gap-2">
                {NAUSEA_OPTIONS.map((opt) => (
                  <Pressable
                    key={opt}
                    onPress={() => {
                      capturePress(pressLabel(opt), "nausea_option");
                      setNausea(opt);
                    }}
                    className={`flex-1 items-center rounded-xl py-2.5 ${
                      nausea === opt
                        ? opt === "severe"
                          ? "border border-red-500/40 bg-red-500/20"
                          : "border border-[#C4B5FD]/40 bg-[#C4B5FD]/20"
                        : "border border-white/10 bg-white/5"
                    }`}
                  >
                    <Text
                      className={`text-xs capitalize ${
                        nausea === opt
                          ? opt === "severe"
                            ? "text-red-400"
                            : "text-[#C4B5FD]"
                          : "text-slate-500"
                      }`}
                    >
                      {opt}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Symptoms */}
            <View>
              <Text className="mb-2 text-sm font-semibold text-slate-300">
                Other Symptoms
              </Text>
              <View className="flex-row flex-wrap gap-2">
                {SYMPTOM_OPTIONS.map((s) => (
                  <Pressable
                    key={s}
                    onPress={() => {
                      capturePress(pressLabel(s), "symptom_chip");
                      toggleSymptom(s);
                    }}
                    className={`rounded-full px-3 py-1.5 ${
                      symptoms.has(s)
                        ? "border border-[#C4B5FD]/40 bg-[#C4B5FD]/20"
                        : "border border-white/10 bg-white/5"
                    }`}
                  >
                    <Text
                      className={`text-xs ${
                        symptoms.has(s) ? "text-[#C4B5FD]" : "text-slate-500"
                      }`}
                    >
                      {s}
                    </Text>
                  </Pressable>
                ))}
              </View>
            </View>

            {/* Notes */}
            <View>
              <Text className="mb-1.5 text-xs font-medium text-slate-400">
                Notes
              </Text>
              <TextInput
                value={notes}
                onChangeText={setNotes}
                placeholder="How are you feeling overall?"
                placeholderTextColor="rgba(255,255,255,0.2)"
                multiline
                numberOfLines={2}
                className="rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white"
                style={{ textAlignVertical: "top", minHeight: 50 }}
              />
            </View>
          </ScrollView>

          {/* Save */}
          <View className="border-t border-white/10 px-5 py-4">
            {saveError ? (
              <Text className="text-center text-sm text-rose-400">
                {"Couldn't save your check-in — it's still here, try again."}
              </Text>
            ) : null}
            <Pressable
              onPress={handleSave}
              disabled={saveState !== "idle"}
              className={`items-center rounded-2xl py-3.5 ${
                saveState === "saved" ? "bg-green-500" : "bg-amber-500"
              }`}
              style={{ opacity: saveState === "saving" ? 0.7 : 1 }}
            >
              {saveState === "saving" ? (
                <View className="flex-row items-center gap-2">
                  <Loader2 size={16} color="#0f172a" />
                  <Text className="text-sm font-semibold text-slate-900">
                    Saving...
                  </Text>
                </View>
              ) : saveState === "saved" ? (
                <View className="flex-row items-center gap-2">
                  <Check size={16} color="#ffffff" />
                  <Text className="text-sm font-semibold text-white">
                    Saved!
                  </Text>
                </View>
              ) : (
                <Text className="text-sm font-semibold text-slate-900">
                  Save Check-in
                </Text>
              )}
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}
